import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  WrenchOutlinedIcon,
  BeakerOutlinedIcon,
  HouseOutlinedIcon,
  LightbulbOutlinedIcon,
  HeartOutlinedIcon,
} from '../../components/icons'
import { AppDispatch } from '../../store'
import { UserSelector } from '../../store/User'
import {
  WaterQualityIndexActions,
  WaterQualityIndexSelectors,
  UIStatus,
  UICategory,
  GroupedCategory,
} from '../../store/waterQualityIndex'
import { WaterTestKitResultsSelector } from '../../store/waterTestKitResults'
import { WaterTestKitRequestsSelectors } from '../../store/waterTestKitRequests'
import { AppliancesSelector } from '../../store/Appliances'
import { WQIParameterResult } from './Components'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useNavigate } from 'react-router'

// Get category icon based on category name
const getCategoryIcon = (category: UICategory) => {
  switch (category) {
    case 'Health and safety':
      return <HeartOutlinedIcon sx={{ fontSize: 20 }} />
    case 'Home infrastructure':
      return <LightbulbOutlinedIcon sx={{ fontSize: 20 }} />
    case 'Skin, hair, laundry':
      return <HouseOutlinedIcon sx={{ fontSize: 20 }} />
    case 'Colour, taste, odor (CTO)':
      return <BeakerOutlinedIcon sx={{ fontSize: 20 }} />
    default:
      return <BeakerOutlinedIcon sx={{ fontSize: 20 }} />
  }
}

// Category descriptions for the detail section
const getCategoryDescription = (category: UICategory): string => {
  switch (category) {
    case 'Health and safety':
      return 'Measures contaminants and pathogens that could affect your health, including lead, bacteria, and chemical compounds.'
    case 'Home infrastructure':
      return 'Monitors factors that can impact your plumbing, appliances, and home water systems over time.'
    case 'Skin, hair, laundry':
      return 'Evaluates mineral content and hardness levels that can affect skin, hair health, and laundry quality.'
    case 'Colour, taste, odor (CTO)':
      return 'Assesses aesthetic water quality factors including discoloration, unusual tastes, and odors.'
    default:
      return ''
  }
}

// Map UIStatus to chip config
const getStatusChipConfig = (status: UIStatus, theme: any) => {
  switch (status) {
    case 'unsafe':
      return {
        label: 'Take action',
        color: theme.palette.error.main,
        backgroundColor: theme.palette.error.light,
      }
    case 'cautious':
      return {
        label: 'Cautious',
        color: theme.palette.warning.main,
        backgroundColor: theme.palette.warning.light,
      }
    case 'safe':
    default:
      return {
        label: 'Safe',
        color: theme.palette.success.main,
        backgroundColor: theme.palette.success.light,
      }
  }
}

// Get overall status chip config
const getOverallStatusConfig = (status: UIStatus, theme: any) => {
  switch (status) {
    case 'unsafe':
      return {
        label: 'Unsafe',
        chipVariant: 'outlined-error' as const,
      }
    case 'cautious':
      return {
        label: 'Cautious',
        chipVariant: 'outlined-warning' as const,
      }
    case 'safe':
    default:
      return {
        label: 'Safe',
        chipVariant: 'outlined-success' as const,
      }
  }
}

export const InsightsPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  // Selected category for details view
  const [selectedCategory, setSelectedCategory] = useState<GroupedCategory | null>(null)

  // User selector
  const userId = useSelector(UserSelector.userId)

  // WQI data selectors
  const loading = useSelector(WaterQualityIndexSelectors.selectLoading)
  const categoriesData = useSelector(WaterQualityIndexSelectors.selectContributorsByUICategory)
  const waterSafetyStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)

  // Existing selectors for equipment and test results
  const latestTestResult = useSelector(WaterTestKitResultsSelector.makeLatestResult)
  const testSummary = useSelector(WaterTestKitResultsSelector.makeLatestResultsSummaryShort)
  const latestTestRequest = useSelector(WaterTestKitRequestsSelectors.makeLatestResult)
  const waterFiltration = useSelector(AppliancesSelector.waterFiltrationOptions)

  useEffect(() => {
    if (userId) {
      dispatch(WaterQualityIndexActions.fetchWaterQualityIndexAsync({ userId }))
    }
  }, [userId, dispatch])

  const overallStatusConfig = getOverallStatusConfig(waterSafetyStatus, theme)

  return (
    <Grid
      sx={{ py: 4, px: 2, width: '100%', minWidth: '100%', background: 'white' }}
      container
      flexWrap="nowrap"
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      spacing={2}
    >
      <Typography variant="h4">Insights</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Overall water status */}
          <Box
            sx={{
              width: '100%',
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
              p: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="h5" fontWeight={600}>
                Overall water status
              </Typography>
              <Chip
                label={overallStatusConfig.label}
                size="small"
                variant={overallStatusConfig.chipVariant}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              This score is based on real-time monitoring, quarterly test results, and long-term
              trends across multiple water quality signals.
            </Typography>
          </Box>

          {/* Current equipment and Latest test cards */}
          <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
            {/* Current equipment card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        backgroundColor: theme.palette.success.bgcolor || theme.palette.green[50],
                        borderRadius: 1,
                        p: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <WrenchOutlinedIcon color="success" sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      Current equipment
                    </Typography>
                  </Stack>

                  <Stack spacing={1.5}>
                    {latestTestRequest &&
                      latestTestRequest.systems &&
                      latestTestRequest.systems.map((item) => {
                        const _item =
                          waterFiltration.find((filtration) => filtration.value === item) ||
                          ({} as any)
                        return (
                          <Stack
                            key={item}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2">{_item.label}</Typography>
                            <Chip
                              label="Active"
                              size="small"
                              variant="outlined-success"
                            />
                          </Stack>
                        )
                      })}
                  </Stack>

                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      mt: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    View details <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* Latest test card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 1,
                        p: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BeakerOutlinedIcon sx={{ fontSize: 20, color: theme.palette.grey[600] }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      Latest test
                    </Typography>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Test date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {latestTestResult?.uploadedAt
                          ? new Date(latestTestResult.uploadedAt).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Parameters tested
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {testSummary
                          ? `${Object.values(testSummary).reduce((acc, val) => acc + (val as any).count, 0)} of 15`
                          : '0 of 15'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Issues found
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {testSummary
                          ? Object.entries(testSummary).reduce((acc, [k, v]) => {
                              if (['Low', 'High'].includes(k)) {
                                acc += (v as any).count
                              }
                              return acc
                            }, 0)
                          : 0} Areas
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      mt: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                    onClick={() => navigate('/profile/results')}
                  >
                    View full result <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Your personalized insights */}
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
              Your personalized insights
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Based on your water test results and home profile, here are your priority actions
            </Typography>

            {/* Category cards grid */}
            <Grid container spacing={2}>
              {categoriesData.map((category: GroupedCategory) => {
                const chipConfig = getStatusChipConfig(category.status, theme)
                const icon = getCategoryIcon(category.uiCategory)
                const isSelected = selectedCategory?.uiCategory === category.uiCategory

                return (
                  <Grid key={category.uiCategory} size={{ xs: 12, sm: 6 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        border: isSelected
                          ? `2px solid ${theme.palette.primary.main}`
                          : 'none',
                        backgroundColor: isSelected ? '#E6EEF4' : '#F3F6F8',
                      }}
                    >
                      <CardActionArea onClick={() => setSelectedCategory(category)} sx={{ p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ color: theme.palette.grey[600], display: 'flex' }}>
                              {icon}
                            </Box>
                            <Typography variant="body1" fontWeight={500}>
                              {category.uiCategory === 'Colour, taste, odor (CTO)'
                                ? 'CTO: Color, Taste, Odor'
                                : category.uiCategory}
                            </Typography>
                          </Stack>
                          <Chip
                            label={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <div
                                  style={{
                                    width: '5px',
                                    height: '5px',
                                    borderRadius: '50%',
                                    backgroundColor: 'currentColor',
                                    marginRight: '4px',
                                  }}
                                />
                                {chipConfig.label}
                              </Stack>
                            }
                            size="small"
                            sx={{
                              color: chipConfig.color,
                              backgroundColor: chipConfig.backgroundColor,
                            }}
                          />
                        </Stack>
                      </CardActionArea>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </Box>

          {/* Insight details section */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            {selectedCategory ? (
              <Box sx={{ width: '100%' }}>
                <Stack spacing={0.5} sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {selectedCategory.uiCategory}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getCategoryDescription(selectedCategory.uiCategory)}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', mb: 2, display: 'block' }}
                >
                  {selectedCategory.count} {selectedCategory.count === 1 ? 'Signal' : 'Signals'}
                </Typography>
                <Stack spacing={1}>
                  {selectedCategory.contributors.map((contributor, index) => (
                    <WQIParameterResult
                      key={`${(contributor as any).parameter || (contributor as any).contaminantCode}_${index}`}
                      contributor={contributor}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <>
                <InfoOutlinedIcon strokeWidth="1px" sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                  Insight details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select one of the cards above to see more info
                </Typography>
              </>
            )}
          </Box>
        </>
      )}
    </Grid>
  )
}
